// ─────────────────────────────────────────────────────────────────
//  PatientOverview.jsx  —  Atlantis Clinical Dashboard v3
//  "Clinical Clarity" enhanced look:
//   • Gradient hero card with animated ECG trace (signature element)
//   • Glass progress ring embedded in the hero
//   • Stat cards with icon chips + accent reveal on hover
//   • List rows with gradient date-tiles and slide-in accent bar
//  Props unchanged: { patient, visits, decoded, appointments,
//                     onViewAll, onViewAllAppointments, onDetails }
// ─────────────────────────────────────────────────────────────────
import React, { useMemo } from "react";
import { Typography, Progress, Row, Col } from "antd";
import {
  ArrowRightOutlined,
  ExperimentOutlined,
  HistoryOutlined,
  CalendarOutlined,
  FolderOpenOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import {
  G700, G100, G50, GRAY4, GRAY7,
  SLATE, SLATE2, MUTED, WHITE,
  GREEN, AMBER,
  RADIUS_MD, RADIUS_PILL,
  FF_DISPLAY, FF_BODY, FF_MONO,
} from "./patientTokens";
import { StatusBadge } from "./PatientShared";
import { formatVisitDate, visitDateParts } from "./dateUtils";

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

// ─── Appointment status badge ──────────────────────────────────
const AppointmentStatusBadge = ({ status }) => {
  const map = {
    pending:         { color: "#f59e0b", bg: "#fef3c7", label: "Pending" },
    booked:          { color: "#3b82f6", bg: "#eff6ff", label: "Booked" },
    confirmed:       { color: "#10b981", bg: "#d1fae5", label: "Confirmed" },
    checked_in:      { color: "#8b5cf6", bg: "#ede9fe", label: "Checked In" },
    in_consultation: { color: "#6366f1", bg: "#e0e7ff", label: "In Consultation" },
    rescheduled:     { color: "#f59e0b", bg: "#fef3c7", label: "Rescheduled" },
    cancelled:       { color: "#ef4444", bg: "#fee2e2", label: "Cancelled" },
    closed:          { color: "#6b7280", bg: "#f3f4f6", label: "Closed" },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: RADIUS_PILL,
      fontSize: 10, fontWeight: 600, background: s.bg, color: s.color,
      border: `1px solid ${s.color}22`,
      fontFamily: FF_BODY, whiteSpace: "nowrap",
    }}>
      {s.label}
    </span>
  );
};

// ─── Date tile (shared by visits & appointments) ───────────────
const DateTile = ({ day, month }) => (
  <div className="pd-date-tile">
    <span className="pd-dt-day" style={{
      fontSize: 15, fontWeight: 700, color: G700, lineHeight: 1, fontFamily: FF_DISPLAY,
    }}>
      {day}
    </span>
    <span className="pd-dt-mon" style={{
      fontSize: 7.5, fontWeight: 700, color: MUTED, marginTop: 2,
      textTransform: "uppercase", letterSpacing: "0.6px", fontFamily: FF_BODY,
    }}>
      {month}
    </span>
  </div>
);

// ─── Visit list item ───────────────────────────────────────────
function VisitItem({ visit, onDetails }) {
  const { dayNum, month } = visitDateParts(visit.visited_no);
  const fullDate = formatVisitDate(visit.visited_no);

  return (
    <div className="pd-row clickable" onClick={() => onDetails && onDetails(visit)}>
      <DateTile day={dayNum} month={month} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <Text style={{
          fontSize: 13, fontWeight: 600, color: SLATE, display: "block",
          fontFamily: FF_BODY, letterSpacing: "-0.2px",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {fullDate}
        </Text>
        <Text style={{ color: MUTED, fontSize: 11, fontFamily: FF_BODY, display: "block" }}>
          Hospital visit
        </Text>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        <StatusBadge status={visit.status} />
        <ArrowRightOutlined className="pd-row-arrow" style={{ color: GRAY4, fontSize: 12 }} />
      </div>
    </div>
  );
}

// ─── Appointment list item ─────────────────────────────────────
function AppointmentItem({ appointment }) {
  const dayNum = dayjs(appointment.appointment_date).format("D");
  const month = dayjs(appointment.appointment_date).format("MMM").toUpperCase();
  const fullDate = dayjs(appointment.appointment_date).format("ddd, MMM D YYYY");
  const time = appointment.appointment_time?.slice(0, 5) || "";

  return (
    <div className="pd-row">
      <DateTile day={dayNum} month={month} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <Text style={{
            fontSize: 13, fontWeight: 600, color: SLATE,
            fontFamily: FF_BODY, letterSpacing: "-0.2px",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {fullDate}
          </Text>
          {time && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              fontSize: 10.5, fontWeight: 600, color: G700,
              background: G50, border: `1px solid ${G100}`,
              padding: "2px 8px", borderRadius: RADIUS_PILL,
              fontFamily: FF_MONO, flexShrink: 0,
            }}>
              <ClockCircleOutlined style={{ fontSize: 10 }} /> {time}
            </span>
          )}
        </div>
        <Text style={{
          color: MUTED, fontSize: 11, fontFamily: FF_BODY, display: "block",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          Dr. {appointment.doctor_name || "—"} · {appointment.specialty_name || "General"}
        </Text>
      </div>

      <div style={{ flexShrink: 0 }}>
        <AppointmentStatusBadge status={appointment.status} />
      </div>
    </div>
  );
}

// ─── Panel shell ───────────────────────────────────────────────
function Panel({ icon, title, count, onViewAll, showViewAll, children }) {
  return (
    <div className="pd-panel">
      <div style={{
        padding: "14px 18px", borderBottom: `1px solid ${GRAY7}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexShrink: 0, gap: 8,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <span style={{
            width: 30, height: 30, borderRadius: 9, flexShrink: 0,
            background: G50, border: `1px solid ${G100}`, color: G700,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: 14,
          }}>
            {icon}
          </span>
          <Text style={{
            fontSize: 14.5, fontWeight: 700, color: SLATE,
            fontFamily: FF_DISPLAY, letterSpacing: "-0.3px", whiteSpace: "nowrap",
          }}>
            {title}
          </Text>
          {typeof count === "number" && count > 0 && (
            <span style={{
              fontSize: 10.5, fontWeight: 700, color: G700, background: G50,
              border: `1px solid ${G100}`, borderRadius: RADIUS_PILL,
              padding: "1px 8px", fontFamily: FF_MONO,
            }}>
              {count}
            </span>
          )}
        </div>
        {showViewAll && (
          <button className="pd-view-all" onClick={onViewAll}>
            View all <ArrowRightOutlined style={{ fontSize: 10 }} />
          </button>
        )}
      </div>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

// ─── Empty state ───────────────────────────────────────────────
const EmptyState = ({ icon, text, hint }) => (
  <div style={{ padding: "36px 20px", textAlign: "center" }}>
    <div style={{
      width: 44, height: 44, borderRadius: 14, margin: "0 auto 10px",
      background: GRAY7, color: GRAY4, fontSize: 18,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {icon}
    </div>
    <Text style={{ color: SLATE2, fontFamily: FF_BODY, fontSize: 13, fontWeight: 600, display: "block" }}>
      {text}
    </Text>
    {hint && (
      <Text style={{ color: MUTED, fontFamily: FF_BODY, fontSize: 11.5 }}>{hint}</Text>
    )}
  </div>
);

// ─── Main component ──────────────────────────────────────────────
function PatientOverview({
  patient, visits, decoded, appointments = [],
  onViewAll, onViewAllAppointments, onDetails,
}) {
  const totalVisits = visits.length;
  const openVisits = visits.filter((v) => v.status === "OPEN");
  const closedVisits = visits.filter((v) => v.status === "CLOSED");
  const lastVisit = visits[0] || null;

  const progress = totalVisits > 0 ? Math.round((closedVisits.length / totalVisits) * 100) : 0;

  const firstName =
    patient?.full_name?.split(" ")[0] || decoded?.fullName?.split(" ")[0] || "there";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const prn = patient?.prn || decoded?.prn || "—";

  const recentVisits = visits.slice(0, 5);

  const upcomingAppointments = useMemo(() => {
    return appointments
      .filter((a) => {
        const appDate = dayjs(a.appointment_date);
        return appDate.isAfter(dayjs(), "day") && (a.status === "booked" || a.status === "confirmed");
      })
      .sort((a, b) => {
        const dateA = dayjs(a.appointment_date);
        const dateB = dayjs(b.appointment_date);
        if (dateA.isSame(dateB, "day")) {
          return (a.appointment_time || "").localeCompare(b.appointment_time || "");
        }
        return dateA.diff(dateB);
      });
  }, [appointments]);

  const stats = [
    {
      label: "Total visits", value: totalVisits, color: SLATE, accent: G700,
      icon: <HistoryOutlined />,
    },
    {
      label: "Open", value: openVisits.length, color: AMBER, accent: AMBER,
      icon: <FolderOpenOutlined />,
      sub: openVisits.length > 0 ? "Needs follow-up" : "All clear",
    },
    {
      label: "Closed", value: closedVisits.length, color: GREEN, accent: GREEN,
      icon: <CheckCircleOutlined />,
    },
    {
      label: "Last visit",
      value: lastVisit ? dayjs(lastVisit.visited_no).format("MMM D") : "—",
      color: SLATE, accent: G700,
      icon: <CalendarOutlined />,
      sub: lastVisit ? dayjs(lastVisit.visited_no).format("YYYY") : null,
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      {/* ─── Hero: gradient card with ECG signature ───────────── */}
      <div className="pd-hero pd-up" style={{ marginBottom: 20 }}>
        <div className="pd-hero-grid" />

        {/* Heartbeat trace — draws itself on load */}
        <svg
          viewBox="0 0 900 60" preserveAspectRatio="none" aria-hidden="true"
          style={{
            position: "absolute", left: 0, right: 0, bottom: 8,
            width: "100%", height: 54, opacity: 0.5, pointerEvents: "none",
          }}
        >
          <path
            className="pd-ekg-path"
            d="M0,30 L180,30 L205,30 L217,10 L229,50 L241,22 L253,30 L420,30 L445,30 L457,8 L469,52 L481,20 L493,30 L680,30 L705,30 L717,12 L729,48 L741,24 L753,30 L900,30"
            fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.6"
            strokeLinecap="round" strokeLinejoin="round"
          />
        </svg>

        <Row gutter={[24, 20]} align="middle" style={{ padding: "26px 28px 34px", margin: 0 }}>
          <Col xs={24} lg={15} style={{ padding: 0 }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              fontSize: 10.5, fontWeight: 700, letterSpacing: "1.1px",
              textTransform: "uppercase", color: "rgba(255,255,255,0.72)",
              fontFamily: FF_BODY, marginBottom: 8,
            }}>
              <span className="pd-live-dot" />
              {dayjs().format("dddd, MMMM D")}
            </span>

            <Title
              level={2}
              style={{
                margin: 0, fontFamily: FF_DISPLAY, fontWeight: 800,
                fontSize: "clamp(1.55rem, 3.4vw, 2.2rem)",
                color: WHITE, letterSpacing: "-0.7px", lineHeight: 1.15,
              }}
            >
              {greeting}, {firstName}.
            </Title>

            <Text style={{
              color: "rgba(255,255,255,0.78)", fontSize: 14, fontFamily: FF_BODY,
              lineHeight: 1.55, display: "block", marginTop: 8, maxWidth: 480,
            }}>
              {totalVisits > 0
                ? `${totalVisits} visit${totalVisits > 1 ? "s" : ""} on record. Your care plan is updated by your clinical team in real time.`
                : "Your health records are securely stored here."}
            </Text>

            <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
              <button
                className="pd-hero-btn-primary"
                style={{
                  height: 44, padding: "0 20px", borderRadius: RADIUS_MD,
                  fontWeight: 600, fontSize: 13.5, fontFamily: FF_BODY,
                  display: "inline-flex", alignItems: "center", gap: 8,
                }}
                onClick={() => onDetails && onDetails({ _nav: "investigation" })}
              >
                <ExperimentOutlined /> Investigation report
              </button>
              <button
                className="pd-hero-btn-ghost"
                style={{
                  height: 44, padding: "0 20px", borderRadius: RADIUS_MD,
                  fontWeight: 600, fontSize: 13.5, fontFamily: FF_BODY,
                  display: "inline-flex", alignItems: "center", gap: 8,
                }}
                onClick={() => onDetails && onDetails({ _nav: "visits" })}
              >
                <HistoryOutlined /> Visit history
              </button>
            </div>
          </Col>

          {/* Glass progress card inside the hero */}
          <Col xs={24} lg={9} style={{ padding: 0 }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 18,
              background: "rgba(255,255,255,0.10)",
              border: "1px solid rgba(255,255,255,0.22)",
              backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
              padding: "18px 20px", borderRadius: 18, width: "100%",
            }}>
              <Progress
                type="circle"
                percent={progress}
                strokeColor={WHITE}
                trailColor="rgba(255,255,255,0.18)"
                strokeWidth={9}
                size={78}
                format={(p) => (
                  <span style={{ fontWeight: 700, fontSize: 16, color: WHITE, fontFamily: FF_DISPLAY }}>
                    {p}%
                  </span>
                )}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text style={{
                  fontSize: 10.5, color: "rgba(255,255,255,0.65)", fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: "0.8px",
                  fontFamily: FF_BODY, display: "block",
                }}>
                  Care Progress
                </Text>
                <Text style={{
                  fontSize: 13, color: WHITE, fontWeight: 600,
                  fontFamily: FF_BODY, display: "block", marginTop: 2,
                }}>
                  {closedVisits.length} of {totalVisits} visits closed
                </Text>
                <div style={{
                  height: 1, background: "rgba(255,255,255,0.18)", margin: "10px 0 8px",
                }} />
                <Text style={{
                  fontSize: 9.5, color: "rgba(255,255,255,0.55)",
                  fontFamily: FF_MONO, display: "block", letterSpacing: "0.6px",
                }}>
                  PATIENT ID
                </Text>
                <Text style={{
                  fontSize: 13.5, fontWeight: 700, color: WHITE, fontFamily: FF_MONO,
                  display: "block", whiteSpace: "nowrap", overflow: "hidden",
                  textOverflow: "ellipsis",
                }}>
                  {prn}
                </Text>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* ─── Stat cards: 2-up mobile, 4-up from tablet up ───────── */}
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        {stats.map((stat, idx) => (
          <Col key={idx} xs={12} sm={12} md={6} style={{ display: "flex" }}>
            <div
              className={`pd-stat-card pd-up pd-up-${idx + 1}`}
              style={{
                flex: 1, padding: "16px 16px 14px",
                display: "flex", flexDirection: "column", justifyContent: "center",
                "--pd-accent": stat.accent,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <Text style={{
                  fontSize: 10.5, color: MUTED, fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.6px", fontFamily: FF_BODY,
                }}>
                  {stat.label}
                </Text>
                <span style={{
                  width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                  background: `${stat.accent}12`, color: stat.accent,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontSize: 13,
                }}>
                  {stat.icon}
                </span>
              </div>
              <Text className="pd-stat-value" style={{
                fontSize: 26, fontWeight: 700, color: stat.color,
                fontFamily: FF_DISPLAY, display: "block", lineHeight: 1.15,
                letterSpacing: "-0.5px",
              }}>
                {stat.value}
              </Text>
              {stat.sub && (
                <Text style={{ fontSize: 10.5, color: stat.color, fontWeight: 600, fontFamily: FF_BODY, marginTop: 2 }}>
                  {stat.sub}
                </Text>
              )}
            </div>
          </Col>
        ))}
      </Row>

     {/* ─── Recent visits & Appointments ────────────────────── */}
   <Row gutter={[16, 16]}>
  <Col xs={24} lg={12} className="pd-up pd-up-3" style={{ display: "flex" }}>
    <div style={{ width: "100%" }}>
      <Panel
        icon={<HistoryOutlined />}
        title="Recent visits"
        count={totalVisits}
        showViewAll={visits.length > 5}
        onViewAll={onViewAll}
      >
        {visits.length === 0 ? (
          <EmptyState
            icon={<HistoryOutlined />}
            text="No visits on record yet"
            hint="Visits will appear here after your first consultation."
          />
        ) : (
          recentVisits.map((v) => (
            <VisitItem key={v.id} visit={v} onDetails={onDetails} />
          ))
        )}
      </Panel>
    </div>
  </Col>

  <Col xs={24} lg={12} className="pd-up pd-up-4" style={{ display: "flex" }}>
    <div style={{ width: "100%" }}>
      <Panel
        icon={<CalendarOutlined />}
        title="Appointments"
        count={upcomingAppointments.length}
        showViewAll={upcomingAppointments.length > 5}
        onViewAll={onViewAllAppointments}
      >
        {upcomingAppointments.length === 0 ? (
          <EmptyState
            icon={<CalendarOutlined />}
            text="No upcoming appointments"
            hint="Book a visit to see it listed here."
          />
        ) : (
          upcomingAppointments.slice(0, 5).map((app) => (
            <AppointmentItem key={app.id} appointment={app} />
          ))
        )}
      </Panel>
    </div>
  </Col>
</Row>
    </div>
  );
}

export default PatientOverview;